<template>
    <div v-if="authStore.isLoading"
         class="d-flex justify-content-center align-items-center w-100 vh-100">
        <Loader :size="80"
                :thickness="6"
                :variant="LoaderVariantsEnum.DARK_PURPLE" />
    </div>
    <section>
        <DashboardBar v-if="dashboardBarStore.isBarVisible" />
        <Sidebar v-if="authStore.isUserFetchCompleted && !isMobileOrTablet"
                 :isMovedDown="dashboardBarStore.isBarVisible"/>
        <AirdropSeasonNotification v-if="!authStore.isLoading && isAirdropSeasonNotification"
                                   :isMovedDown="dashboardBarStore.isBarVisible" />
        <Navigation v-if="!authStore.isLoading"
                    :isMovedDown="dashboardBarStore.isBarVisible && !isAirdropSeasonNotification" />
        <MobileFooter v-if="isMobileOrTablet" />
        <RouterView v-if="authStore.isUserFetchCompleted"
                    :key="$route.path" />
        <OnboardingModals />
        <NotificationModals />
        <AuthModal v-if="modalStore.authModals.showAuth || modalStore.authModals.showWalletConnect" />
        <EmailVerifyModal v-if="modalStore.modals[ModalEnum.EMAIL_VERIFY]" />
        <CustomErrorModal v-if="modalStore.modals[ModalEnum.CUSTOM_ERROR]" />
        <MetamaskMobileAuthModal v-if="modalStore.modals[ModalEnum.METAMASK_AUTH]" />
        <CoinbaseMobileAuthModal v-if="modalStore.modals[ModalEnum.COINBASE_AUTH]" />
        <ExtensionConnectedModal v-if="modalStore.modals[ModalEnum.EXTENSION_CONNECTED]" />
    </section>
    <CompanionBubble v-if="!authStore.isLoading && !authStore.isUserLoggedIn"
                     isMissionsPage
                     :isRaised="isCompanionRaised" />
    <ForceEmailVerificationModal v-if="modalStore.modals[ModalEnum.FORCE_EMAIL_VERIFICATION]" />
    <BackToGameFooter v-if="shouldShowBackToGameFooter"
                      class="d-none d-md-flex" />
    <BackToGameFooterMobile v-if="shouldShowBackToGameFooterMobile"
                            class="d-flex d-md-none" />
    <ConnectToGainFooter v-if="shouldShowConnectToGainFooter" />
    <ReturnToMobileAppModal v-if="modalStore.modals[ModalEnum.RETURN_TO_MOBILE_APP]" />
    <BrowserNotifications />
    <TangibleReward />
</template>

<script setup lang="ts">
    import {
        computed, defineAsyncComponent, onMounted, ref, watch,
    } from 'vue';
    import { useRoute } from 'vue-router';
    import Loader from '../../components/Loader.vue';
    import Sidebar from './navigation/Sidebar.vue';
    import Navigation from './navigation/Navigation.vue';
    import { useAuthStore } from '@/js/store/auth/AuthStore';
    import { CompanionFooterRaisedEnum } from './types/enums';
    import BackToGameFooter from './components/back-to-game-footer/BackToGameFooter.vue';
    import { useUserStore } from '@/js/store/quests/dashboard/UserStore';
    import { useDashboardStore } from '@/js/store/quests/dashboard/DashboardStore';
    import { ModalEnum, useModalStore } from '@/js/store/quests/game/modules/ModalStore';
    import BackToGameFooterMobile from './components/back-to-game-footer/BackToGameFooterMobile.vue';
    import MobileFooter from './navigation/mobile/footer/MobileFooter.vue';
    import EmailVerifyModal from './modals/settings/EmailVerifyModal.vue';
    import CustomErrorModal from './modals/CustomErrorModal.vue';
    import ForceEmailVerificationModal from './modals/ForceEmailVerificationModal.vue';
    import { Web3ProviderEnum } from '@/js/store/web3/types/enums';
    import { isMobileDevice } from '@/js/auth/helpers/deviceUtils';
    import MetamaskMobileAuthModal from '@/js/auth/modals/mobile/MetamaskMobileAuthModal.vue';
    import CoinbaseMobileAuthModal from '@/js/auth/modals/mobile/CoinbaseMobileAuthModal.vue';
    import { useWeb3Store } from '@/js/store/web3/Web3Store';
    import { useDeviceSize } from '@/js/composables/useDeviceSize';
    import { LoaderVariantsEnum } from '@/js/enums/loaderVariants';
    import ReturnToMobileAppModal from '../../auth/modals/mobile/ReturnToMobileAppModal.vue';
    import { deleteUrlParam } from '@/js/helpers/urlUtils';
    import { QuestQueryParamsEnum } from '../global/types/enums';
    import CompanionBubble from '../../global/top-menu/CompanionBubble.vue';
    import DashboardBar from './components/dashboard-bar/DashboardBar.vue';
    import { useDashboardBarStore } from '@/js/store/quests/dashboard/DashboardBarStore';
    import ConnectToGainFooter from '@/js/quests/dashboard/components/connect-to-gain-footer/ConnectToGainFooter.vue';
    import { COOKIES_KEYS } from '@/js/quests/game/constants/cookiesKeys';
    import { getCookieValue } from '@/js/helpers/cookieHelpers';
    import {
        isReferralCodeStored,
        isReferralCodeInUrl,
        storeReferralCode,
    } from '@/js/quests/dashboard/helpers/referralLinkHelper';
    import AirdropSeasonNotification from '@/js/quests/dashboard/components/AirdropSeasonNotification.vue';
    import OnboardingModals from '@/js/quests/dashboard/components/OnboardingModals.vue';
    import BrowserNotifications from '@/js/quests/dashboard/components/BrowserNotifications.vue';
    import TangibleReward from '@/js/quests/dashboard/components/TangibleReward.vue';
    import { useExtensionConnected } from '@/js/quests/dashboard/composables/useExtensionConnected';
    import ExtensionConnectedModal from '@/js/quests/dashboard/modals/ExtensionConnectedModal.vue';
    import NotificationModals from '@/js/quests/dashboard/components/NotificationModals.vue';

    const AuthModal = defineAsyncComponent(() => import('../../auth/AuthModal.vue'));

    const authStore = useAuthStore();
    const web3Store = useWeb3Store();
    const userStore = useUserStore();
    const dashboardStore = useDashboardStore();
    const modalStore = useModalStore();
    const dashboardBarStore = useDashboardBarStore();
    const route = useRoute();
    const { isMobileOrTablet } = useDeviceSize();
    const { checkExtensionConnected } = useExtensionConnected();

    const isAirdropSeasonNotification = ref(true);

    const isCompanionRaised = computed(() => dashboardStore.isCompanionRaised.isMissionActive
        || dashboardStore.isCompanionRaised.isSelectedMissionFooterShown
        || dashboardStore.isCompanionRaised.isMissionsFooterShown);

    const isMission = computed(() => route.name === 'mission');

    const isActiveMission = computed(() => {
        const { activeUserRoundData, isLoadingActiveUserRoundData } = userStore;

        return !!activeUserRoundData && !isLoadingActiveUserRoundData;
    });

    const shouldShowBackToGameFooter = computed(() => isActiveMission.value && !isMobileOrTablet.value);
    const shouldShowBackToGameFooterMobile = computed(() => isActiveMission.value && isMobileOrTablet.value);
    const shouldShowConnectToGainFooter = computed(() => authStore.isUserFetchCompleted && !authStore.isUserLoggedIn && !isMission.value && isMobileOrTablet.value);

    const handleAuthType = async (authType: string) => {
        if (authType === Web3ProviderEnum.METAMASK || authType === Web3ProviderEnum.COINBASE) {
            web3Store.otpCode = await web3Store.fetchOtp();

            if (authType === Web3ProviderEnum.METAMASK) {
                modalStore.toggleModal(ModalEnum.METAMASK_AUTH, true);
            }

            if (authType === Web3ProviderEnum.COINBASE) {
                modalStore.toggleModal(ModalEnum.COINBASE_AUTH, true);
            }
        }
    };

    watch(() => isActiveMission.value, () => {
        dashboardStore.setIsCompanion(isActiveMission.value, CompanionFooterRaisedEnum.IS_MISSION_ACTIVE);
    }, { immediate: true });

    onMounted(async () => {
        await authStore.fetchUser();

        const queryParams = new URLSearchParams(window.location.search);
        const authType = queryParams.get(QuestQueryParamsEnum.TYPE);
        const isMobileApp = queryParams.get('is-mobile-app');

        if (isMobileApp) {
            web3Store.setIsMobileApp(true);
        }

        if (authType && isMobileDevice()) {
            await handleAuthType(authType);
            deleteUrlParam(QuestQueryParamsEnum.TYPE, queryParams);
        }

        if (getCookieValue(COOKIES_KEYS.CUSTOM_ERROR_MODAL)) {
            modalStore.toggleModal(ModalEnum.CUSTOM_ERROR, true);
        }

        if (authStore.isUserFetchCompleted && !authStore.isUserLoggedIn) {
            if (isReferralCodeInUrl()) {
                storeReferralCode();
            }

            if (isReferralCodeStored()) {
                authStore.isReferralCodeStored = true;
            }
        }

        checkExtensionConnected();
    });
</script>
